import React, { useState, useCallback } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { AlertTriangle } from 'lucide-react';

/**
 * Reusable themed confirmation dialog. Backed by Radix AlertDialog (the same
 * Shadcn primitive already used elsewhere) so it can be rendered from a
 * row-action dropdown without racing the dropdown's pointer-down close logic
 * — which is what breaks `window.confirm()` in Radix DropdownMenu items.
 */

/**
 * `useConfirmDialog` returns `[ConfirmDialog, openConfirm]`. Render the
 * dialog once at the page level, then call `openConfirm({ title, ... })`
 * from any handler. Resolves a Promise<boolean> when the user picks.
 *
 *   const [ConfirmDialog, openConfirm] = useConfirmDialog();
 *   const ok = await openConfirm({
 *     title: 'Delete this patient?',
 *     description: 'This cannot be undone.',
 *     confirmText: 'Delete',
 *     destructive: true,
 *   });
 *   if (!ok) return;
 *   ...do the thing...
 *
 *   return <>{...page...}{ConfirmDialog}</>;
 */
export function useConfirmDialog() {
  const [state, setState] = useState(null);
  // state: { title, description, confirmText, cancelText, destructive, resolve }

  const openConfirm = useCallback((opts) => {
    return new Promise((resolve) => {
      setState({
        title: opts?.title || 'Are you sure?',
        description: opts?.description || '',
        confirmText: opts?.confirmText || 'Confirm',
        cancelText: opts?.cancelText || 'Cancel',
        destructive: !!opts?.destructive,
        testId: opts?.testId || 'confirm-dialog',
        resolve,
      });
    });
  }, []);

  const handleCancel = () => {
    state?.resolve(false);
    setState(null);
  };
  const handleConfirm = () => {
    state?.resolve(true);
    setState(null);
  };

  const open = !!state;

  const dialog = (
    <AlertDialog
      open={open}
      onOpenChange={(v) => {
        if (!v && state) handleCancel();
      }}
    >
      <AlertDialogContent data-testid={state?.testId}>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {state?.destructive ? (
              <AlertTriangle className="h-5 w-5 text-rose-600" />
            ) : null}
            <span>{state?.title}</span>
          </AlertDialogTitle>
          {state?.description ? (
            <AlertDialogDescription>{state.description}</AlertDialogDescription>
          ) : null}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel} data-testid="confirm-dialog-cancel">
            {state?.cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            data-testid="confirm-dialog-confirm"
            className={
              state?.destructive
                ? 'bg-rose-600 hover:bg-rose-700 focus:ring-rose-500'
                : undefined
            }
          >
            {state?.confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return [dialog, openConfirm];
}
